import rtcstatsInit from '@jitsi/rtcstats';
import logger from './logger';
import { RTCStatsTelemetryCollector } from './rtcstatsAudio';
const DEFAULT_RTCSTATS_POLL_INTERVAL_MS = 10_000;
let telemetryCollector;
/**
 * Reconstructs and forwards allowlisted audio and connectivity telemetry through the iframe External API.
 *
 * @param {string} statsType - The rtcstats event type.
 * @param {string | null} pcId - The rtcstats PeerConnection identifier.
 * @param {unknown} data - The rtcstats event data.
 * @returns {void}
 */
function forwardStatsEntry(statsType, pcId, data) {
    if (!telemetryCollector) {
        return;
    }
    let entries = [];
    try {
        entries = telemetryCollector?.process(statsType, pcId, data) ?? [];
    }
    catch (error) {
        logger.warn('Dropping rtcstats entry that could not be reconstructed', error);
        return;
    }
    if (!entries?.length || typeof APP !== 'object' || !APP.API?.notifyRTCStatsEntry) {
        return;
    }
    entries.forEach(entry => {
        APP.API.notifyRTCStatsEntry({
            ...entry,
            pcId,
            timestamp: Date.now()
        });
    });
}
/**
 * Clears reconstructed rtcstats reports when the current conference ends.
 *
 * @returns {void}
 */
export function clearRTCStatsExternalApiCache() {
    telemetryCollector?.clear();
}
/**
 * Forwards a remote audio play() failure or recovery through the iframe External API when rtcstats External API
 * collection is enabled.
 *
 * @param {Object} event - The playback phase, retry details and the play() rejection, if any.
 * @returns {void}
 */
export function notifyAudioPlaybackEvent({ error, ...event }) {
    if (!telemetryCollector || typeof APP !== 'object' || !APP.API?.notifyRTCStatsEntry) {
        return;
    }
    const activation = navigator.userActivation;
    APP.API.notifyRTCStatsEntry({
        data: {
            ...event,
            errorMessage: error?.message,
            errorName: error?.name,
            userActivation: activation && (activation.isActive ? 'transient' : activation.hasBeenActive ? 'sticky' : 'none'),
            visibilityState: document.visibilityState
        },
        pcId: null,
        statsType: 'audio-playback-event',
        timestamp: Date.now()
    });
}
/**
 * Initializes the web-only rtcstats bridge once and updates whether entries should be forwarded.
 *
 * @param {Object} analytics - The current analytics configuration.
 * @param {Function} getState - Returns the current Redux state for track correlation.
 * @returns {void}
 */
export function initRTCStatsExternalApi(analytics, getState) {
    const externalApiEnabled = analytics?.rtcstatsExternalApiEnabled === true;
    const rtcstatsServerEnabled = analytics?.rtcstatsEnabled === true;
    if (externalApiEnabled && rtcstatsServerEnabled) {
        logger.warn('rtcstats External API collection requires rtcstatsEnabled to be false');
        return;
    }
    if (!externalApiEnabled || telemetryCollector) {
        return;
    }
    telemetryCollector = new RTCStatsTelemetryCollector(getState);
    try {
        rtcstatsInit({ statsEntry: forwardStatsEntry }, {
            pollInterval: analytics?.rtcstatsPollInterval ?? DEFAULT_RTCSTATS_POLL_INTERVAL_MS,
            sendSdp: false,
            useLegacy: false
        });
    }
    catch (error) {
        telemetryCollector = undefined;
        logger.error('Failed to initialize rtcstats External API collection', error);
    }
}
